import React, { useState, useEffect, useMemo } from 'react';
import { QUESTION_TYPES } from './questionTypes';
import LocationAutocomplete from './../../../LocationAutocomplete';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImageUploader from './../../../ImageUploader';
import { v4 as uuidv4 } from 'uuid';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

// Shadcn/ui and lucide-react imports
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle }  from '../../../../components/ui/card';
import { useToast } from '../../../../hooks/use-toast'; // Using shadcn/ui useToast from your specified path
import { GripVertical, XIcon, PlusCircle, SaveIcon, ArrowDownAZ, RotateCcw } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { useJsApiLoader } from '@react-google-maps/api';
import config from '../../../../config';

const MAP_LIBRARIES = ['drawing', 'places', 'marker'];

const slugify = (text) => text ? text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') : '';

function SortableOption({ opt, idx, onChange, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: opt.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    
    
    const handleLabelChange = (e) => {
        const newLabel = e.target.value;
        const newSlug = slugify(newLabel);
        onChange(idx, null, { ...opt, label: newLabel, value: newSlug });
    };
      
    const handleValueInputChange = (e) => {
        const manuallyEnteredValue = slugify(e.target.value);
        onChange(idx, 'value', manuallyEnteredValue);
    };

    const isInactive = opt.is_active === false;

    return (
        <div ref={setNodeRef} style={style} className={`flex gap-2 items-center mb-2 p-3 border border-border rounded-md bg-background${isInactive ? ' opacity-50 bg-muted/30' : ''}`}>
            <Button variant="ghost" size="icon" type="button" {...attributes} {...listeners} className="cursor-move p-1" aria-label="Drag to reorder option">
                <GripVertical className="h-5 w-5 text-muted-foreground" />
            </Button>
            <span className="text-sm text-muted-foreground w-6 select-none">{idx + 1}.</span>
            <Input
                type="text"
                placeholder="Option Label"
                value={opt.label ?? ''}
                onChange={handleLabelChange}
                className="flex-1"
            />
            <Input
                type="text"
                placeholder="Value (auto-slugified)"
                value={opt.value ?? ''}
                onChange={handleValueInputChange}
                className="flex-1"
            />
            {isInactive && <Badge variant="secondary" className="text-xs whitespace-nowrap">Inactive</Badge>}
            {isInactive ? (
                <Button variant="ghost" size="icon" type="button" onClick={() => onChange(idx, 'is_active', true)} className="text-green-600 hover:text-green-700 p-1" aria-label="Reactivate option" title="Reactivate option">
                    <RotateCcw className="h-5 w-5" />
                </Button>
            ) : (
                <Button variant="ghost" size="icon" type="button" onClick={() => onRemove(idx)} className="text-destructive hover:text-destructive p-1" aria-label="Remove option">
                    <XIcon className="h-5 w-5" />
                </Button>
            )}
            {opt.value === 'other' && opt.isUnremovable && (
                <Input
                    type="text"
                    placeholder="Default text for 'Other' (optional)"
                    value={opt.other_placeholder || ''}
                    onChange={(e) => onChange(idx, 'other_placeholder', e.target.value)}
                    className="ml-2 flex-1"
                />
            )}
        </div>
    );
}
  
function QuestionForm({ mode = 'create', initialData = {}, onClose, onAddToDraftList, onSave }) {
  const getInitialFormState = (initData) => ({
    label: '', value: '', question_type: 'text', is_required: false, seller_visible: true,
    order_index: initData.order_index || 0, conditional: false, config: {}, nudge_text: '',
    ab_variant: initData.ab_variant ?? null,
    ...initData,
    options: initData.options
        ? initData.options
            .slice() // Create a copy to be safe
            .sort((a, b) => a.order_index - b.order_index) // Sort by order_index
            .map(opt => ({ ...opt, id: opt.id || uuidv4() })) // Then map to add IDs
        : [],
    images: initData.images ? [...initData.images] : [],
  });
    
    const [form, setForm] = useState(getInitialFormState(initialData));
    const [initialFormSnapshot, setInitialFormSnapshot] = useState(getInitialFormState(initialData));
    const { toast } = useToast(); // Correctly using useToast from shadcn/ui
    const { isLoaded, loadError } = useJsApiLoader({
            id: 'google-map-script',
            googleMapsApiKey: config.googleApiKey,
            libraries: MAP_LIBRARIES,
            region: 'IN',
            version: 'weekly',
        });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        const newInitialState = getInitialFormState(initialData);
        setForm(newInitialState);
        setInitialFormSnapshot(newInitialState);
    }, [initialData]);
    
    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialFormSnapshot), [form, initialFormSnapshot]);

    const handleChange = (eOrValue, fieldName) => {
        let name, value, type, checked;
    
        if (typeof eOrValue === 'object' && eOrValue && eOrValue.target) {
            name = eOrValue.target.name;
            value = eOrValue.target.value;
            type = eOrValue.target.type;
            checked = eOrValue.target.checked;
        } else {
            name = fieldName;
            value = eOrValue;
            if (['is_required', 'seller_visible', 'conditional'].includes(String(name))) {
                type = 'checkbox'; checked = Boolean(eOrValue);
            }
        }
    
        let newValue = value;
        let derivedSlugObject = {};
    
        if (name === 'label') {
            newValue = value;
            if (!form.value || form.value === slugify(form.label)) {
                derivedSlugObject = { value: slugify(newValue) };
            }
        } else if (name === 'value') {
            newValue = slugify(value);
        }
    
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : newValue, ...derivedSlugObject }));
    };

  const handleConfigChange = (key, value) => {
    setForm(prev => ({ ...prev, config: { ...(prev.config || {}), [key]: value } }));
  };

  const handleOptionChange = (index, field, valueOrObject) => {
    setForm((prev) => {
        const updatedOptions = [...prev.options];
        if (field === null && typeof valueOrObject === 'object') {
          updatedOptions[index] = { ...updatedOptions[index], ...valueOrObject, id: updatedOptions[index].id || uuidv4() };
        } else {
          updatedOptions[index] = { ...updatedOptions[index], id: updatedOptions[index].id || uuidv4(), [field]: valueOrObject };
        }
        return { ...prev, options: updatedOptions };
    });
  };
  
  const addOption = () => {
    setForm(prev => ({ ...prev, options: [...prev.options, { id: uuidv4(), label: '', value: '' }] }));
  };

  const removeOption = (index) => {
    setForm(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  const onSortEnd = (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;
    setForm(prev => {
        const oldIndex = prev.options.findIndex(opt => opt.id === active.id);
        const newIndex = prev.options.findIndex(opt => opt.id === over.id);
        return {...prev, options: arrayMove(prev.options, oldIndex, newIndex)};
    });
  };

  const sortOptionsAlphabetically = () => {
    setForm(prev => {
      // Find and separate the 'other' option, if it exists
      const otherOption = prev.options.find(opt => opt.value === 'other');
      const sortableOptions = prev.options.filter(opt => opt.value !== 'other');

      // Sort the remaining options by their label, alphabetically
      sortableOptions.sort((a, b) => a.label.localeCompare(b.label));
      
      // Re-add the 'other' option at the end of the sorted list
      const newSortedOptions = otherOption ? [...sortableOptions, otherOption] : sortableOptions;

      return { ...prev, options: newSortedOptions };
    });
  };

  const addOtherOption = () => {
    if (!form.options.some(opt => opt.value === 'other')) {
      handleConfigChange('allow_other', true);
      setForm(prev => ({
        ...prev,
        options: [...prev.options, { id: uuidv4(), label: 'Other', value: 'other', isUnremovable: true }]
      }));
    }
  };

  const handleLocationSelect = (locationData, address) => {
    handleConfigChange('selected_address', address);
    handleConfigChange('location_details', locationData);
  };

  const imageInitialUrls = useMemo(() => form.images?.filter(img => typeof img === 'string') || [], [form.images]);
  const selectedType = useMemo(() => QUESTION_TYPES.find(t => t.value === form.question_type), [form.question_type]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.label.trim()) {
        toast({ variant: "destructive", title: "Validation Error", description: "Label is required." }); // shadcn/ui toast
        return;
    }
    if (selectedType?.hasOptions && form.options.some(opt => !opt.label.trim())) {
        toast({ variant: "destructive", title: "Validation Error", description: "All options must have a label." }); // shadcn/ui toast
        return;
    }
    
    const formDataToSave = { ...form, id: form.id || initialData.id || uuidv4() };

    //to match options reordering visually. 
    if (formDataToSave.options) {
      formDataToSave.options = formDataToSave.options.map((opt, index) => ({
        ...opt,
        order_index: index,
      }));
    }

    if (mode === 'edit') {
        onSave(formDataToSave);
        // Success toast for edit is typically handled by the parent after API call
    } else { // 'create' mode
        onAddToDraftList(formDataToSave);
        // Reset form for next entry
        const nextOrderIndex = form.order_index + 1; // Prepare next order_index for user convenience
        const freshFormState = getInitialFormState({ order_index: nextOrderIndex });
        setForm(freshFormState);
        setInitialFormSnapshot(freshFormState); 
        // Success toast for adding to draft is typically handled by the parent
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-1">
      <div>
        <Label htmlFor="label">Label</Label>
        <Input id="label" name="label" value={form.label} onChange={handleChange} placeholder="e.g., What is your favorite color?" />
      </div>

      <div>
        <Label htmlFor="value">Slug (internal value)</Label>
        <Input id="value" name="value" value={form.value} onChange={handleChange} placeholder="e.g., favorite_color (auto-generated)" />
        <p className="text-xs text-muted-foreground mt-1">Auto-generated from label if empty or matching. Can be set manually.</p>
      </div>
      <div>
        <Label htmlFor="nudge_text">Nudge Text (Optional)</Label>
        <Input
          id="nudge_text"
          name="nudge_text"
          value={form.nudge_text || ''}
          onChange={handleChange}
          placeholder="This will help users better understand this question"
        />
        <p className="text-xs text-muted-foreground mt-1">This text appears below the label in the user form.</p>
      </div>

      <div>
        <Label htmlFor="question_type">Question Type</Label>
        <Select name="question_type" value={form.question_type} onValueChange={(val) => handleChange(val, 'question_type')}>
            <SelectTrigger id="question_type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
                {QUESTION_TYPES.map(qt => <SelectItem key={qt.value} value={qt.value}>{qt.label}</SelectItem>)}
            </SelectContent>
        </Select>
      </div>

      <div className="space-y-3 pt-2">
        <div className="flex items-center space-x-3">
          <Checkbox id="is_required" name="is_required" checked={form.is_required} onCheckedChange={(checked) => handleChange(checked, 'is_required')} />
          <Label htmlFor="is_required" className="font-normal cursor-pointer">This question is required</Label>
        </div>
        <div className="flex items-center space-x-3">
          <Checkbox id="seller_visible" name="seller_visible" checked={form.seller_visible} onCheckedChange={(checked) => handleChange(checked, 'seller_visible')} />
          <Label htmlFor="seller_visible" className="font-normal cursor-pointer">Visible to Seller</Label>
        </div>
        <div className="flex items-center space-x-3">
          <Checkbox 
            id="conditional" name="conditional" checked={form.conditional} 
            onCheckedChange={(checked) => handleChange(checked, 'conditional')} 
            //disabled={!(selectedType?.canBeConditionalTarget)}
          />
          <Label htmlFor="conditional" className={`font-normal cursor-pointer`}>
            Conditional Question
          </Label>
        </div>
      </div>

      <div>
        <Label htmlFor="ab_variant">A/B Test Variant</Label>
        <Select
          name="ab_variant"
          value={form.ab_variant || 'common'}
          onValueChange={(val) => handleChange(val === 'common' ? null : val, 'ab_variant')}
        >
          <SelectTrigger id="ab_variant"><SelectValue placeholder="Common" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="common">Common (shown in both)</SelectItem>
            <SelectItem value="control">Control only</SelectItem>
            <SelectItem value="test">Test only</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Use "Duplicate as Test Variant" on the question list to create a Control/Test pair instead of tagging manually.
        </p>
      </div>

      {form.question_type === 'date' && (
        <Card>
            <CardHeader><CardTitle className="text-md">Date Options</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <Label htmlFor="end_date">Select Date (Optional default)</Label>
                    <Input type="date" id="end_date" value={form.config?.end_date || ''}
                        onChange={e => handleConfigChange('end_date', e.target.value)}
                        disabled={form.config?.one_year === true}
                    />
                </div>
                <div className="flex items-center space-x-3">
                    <Checkbox id="one_year" checked={form.config?.one_year || false} onCheckedChange={(checked) => handleConfigChange('one_year', checked)} />
                    <Label htmlFor="one_year" className="font-normal cursor-pointer">End date is up to 1 year from today</Label>
                </div>
                <div className="flex items-center space-x-3">
                    <Checkbox id="include_time" checked={form.config?.include_time || false} onCheckedChange={(checked) => handleConfigChange('include_time', checked)} />
                    <Label htmlFor="include_time" className="font-normal cursor-pointer">Include Time Input</Label>
                </div>
            </CardContent>
        </Card>
      )}

      {form.question_type === 'photo_select' && (
        <Card>
            <CardHeader><CardTitle className="text-md">Photo Upload</CardTitle></CardHeader>
            <CardContent>
                <ImageUploader
                    label="Upload Photos (Max 250KB each recommended)"
                    multiple={true}
                    initialUrls={imageInitialUrls}
                    onChange={(filesOrUrls) => setForm(prev => ({ ...prev, images: filesOrUrls }))}
                    note="Users can select one or more photos."
                />
            </CardContent>
        </Card>
      )}

      {form.question_type === 'likert_scale' && (
         <Card>
            <CardHeader><CardTitle className="text-md">Likert Scale Options</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="likert_min_val">Min Value (e.g., 1)</Label>
                        <Input type="number" id="likert_min_val" value={form.config?.min || 1} onChange={e => handleConfigChange('min', parseInt(e.target.value))} />
                    </div>
                    <div>
                        <Label htmlFor="likert_min_label">Min Value Label</Label>
                        <Input type="text" id="likert_min_label" value={form.config?.min_label || ''} onChange={e => handleConfigChange('min_label', e.target.value)} placeholder="e.g., Strongly Disagree"/>
                    </div>
                    <div>
                        <Label htmlFor="likert_max_val">Max Value (e.g., 5)</Label>
                        <Input type="number" id="likert_max_val" value={form.config?.max || 5} max="5" onChange={e => handleConfigChange('max', parseInt(e.target.value))} />
                    </div>
                    <div>
                        <Label htmlFor="likert_max_label">Max Value Label</Label>
                        <Input type="text" id="likert_max_label" value={form.config?.max_label || ''} onChange={e => handleConfigChange('max_label', e.target.value)} placeholder="e.g., Strongly Agree"/>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">Typical scales are 1-5 or 1-7.</p>
            </CardContent>
        </Card>
      )}

      {form.question_type === 'range' && (
        <Card>
            <CardHeader><CardTitle className="text-md">Range Slider Options</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><Label htmlFor="range_min">Min</Label><Input type="number" id="range_min" value={form.config?.min ?? 0} onChange={e => handleConfigChange('min', parseFloat(e.target.value))} /></div>
                    <div><Label htmlFor="range_max">Max</Label><Input type="number" id="range_max" value={form.config?.max ?? 100} onChange={e => handleConfigChange('max', parseFloat(e.target.value))} /></div>
                    <div><Label htmlFor="range_step">Step</Label><Input type="number" id="range_step" value={form.config?.step ?? 1} onChange={e => handleConfigChange('step', parseFloat(e.target.value))} /></div>
                </div>
            </CardContent>
        </Card>
      )}

      {selectedType?.hasOptions && (
        <Card>
            <CardHeader><CardTitle className="text-md">Options for "{selectedType.label}"</CardTitle></CardHeader>
            <CardContent>
                {form.options.length === 0 && <p className="text-sm text-muted-foreground mb-3">No options added yet.</p>}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSortEnd}>
                    <SortableContext items={form.options.map(opt => opt.id)} strategy={verticalListSortingStrategy}>
                    {form.options.map((opt, idx) => <SortableOption key={opt.id} opt={opt} idx={idx} onChange={handleOptionChange} onRemove={removeOption} />)}
                    </SortableContext>
                </DndContext>
                <div className="mt-4 flex flex-wrap gap-3">
                    <Button variant="outline" size="sm" onClick={addOption} type="button"><PlusCircle className="mr-2 h-4 w-4" /> Add Option</Button>
                    {selectedType.allowOther && !form.options.some(opt => opt.value === 'other') && (
                        <Button variant="outline" size="sm" onClick={addOtherOption} type="button"><PlusCircle className="mr-2 h-4 w-4" /> Add "Other" Option</Button>
                    )}
                    <Button variant="outline" size="sm" onClick={sortOptionsAlphabetically} type="button" disabled={form.options.length < 2}>
                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort A-Z
                    </Button>
                </div>
            </CardContent>
        </Card>
      )}

      {form.question_type === 'location' && (
        <Card>
            <CardHeader><CardTitle className="text-md">Location Input</CardTitle></CardHeader>
            <CardContent>
                <LocationAutocomplete isLoaded={isLoaded} loadError={loadError} onSelect={handleLocationSelect} />
                {form.config?.selected_address && <p className="text-sm text-muted-foreground mt-2">Selected: {form.config.selected_address}</p>}
            </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3 pt-6 border-t border-border mt-8">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={(mode === 'edit' && !isDirty)}>
            {mode === 'edit' ? <><SaveIcon className="mr-2 h-4 w-4" /> Save Changes</> : <><PlusCircle className="mr-2 h-4 w-4" /> Add to List</>}
        </Button>
      </div>
    </form>
  );
}
export default React.memo(QuestionForm);
